import { useState, useCallback, memo } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export type EditableCellValue = string | number | boolean | null;

interface EditableCellProps {
  value: EditableCellValue;
  rowId: string;
  column: string;
  onUpdate: (id: string, column: string, value: EditableCellValue) => Promise<void>;
  type?: "text" | "number" | "date" | "select" | "checkbox";
  options?: string[];
  className?: string;
}

const normalizeValue = (value: EditableCellValue): EditableCellValue =>
  value ?? "";

export const EditableCell = memo(function EditableCell({
  value: initialValue,
  rowId,
  column,
  onUpdate,
  type = "text",
  options = [],
  className,
}: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState<EditableCellValue>(
    normalizeValue(initialValue),
  );
  const [isLoading, setIsLoading] = useState(false);

  const displayValue = normalizeValue(initialValue);

  const startEditing = useCallback(() => {
    setDraftValue(displayValue);
    setIsEditing(true);
  }, [displayValue]);

  const handleSave = useCallback(
    async (newValue: EditableCellValue) => {
      setIsEditing(false);

      if (newValue === displayValue) return;

      setIsLoading(true);
      try {
        await onUpdate(rowId, column, newValue);
      } catch (error) {
        console.error("Failed to save", error);
      } finally {
        setIsLoading(false);
      }
    },
    [displayValue, onUpdate, rowId, column],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave(draftValue);
      }
      if (e.key === "Escape") {
        setIsEditing(false);
        setDraftValue(displayValue);
      }
    },
    [handleSave, draftValue, displayValue],
  );

  if (type === "checkbox") {
    return (
      <div className={cn("flex justify-center", className)}>
        <Checkbox
          checked={!!initialValue}
          onCheckedChange={(checked) => handleSave(checked === true)}
          disabled={isLoading}
          className="cursor-pointer"
        />
      </div>
    );
  }

  if (isEditing) {
    if (type === "select") {
      return (
        <Select
          defaultOpen={true}
          onOpenChange={(open) => {
            if (!open) {
              setIsEditing(false);
            }
          }}
          value={String(draftValue)}
          onValueChange={(val) => handleSave(val)}
        >
          <SelectTrigger className={cn("h-8 w-full min-w-[140px]", className)}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <Input
        autoFocus
        value={String(draftValue)}
        onChange={(e) => setDraftValue(e.target.value)}
        onBlur={() => handleSave(draftValue)}
        onKeyDown={handleKeyDown}
        type={type === "date" ? "date" : "text"}
        className={cn("h-8 w-full min-w-[100px]", className)}
      />
    );
  }

  const isEmpty =
    displayValue === "" || displayValue === null || displayValue === undefined;

  return (
    <div
      onClick={startEditing}
      className={cn(
        "cursor-pointer px-2 py-1 rounded min-h-[28px] flex items-center transition-colors truncate",
        isLoading && "pointer-events-none bg-muted/50 opacity-50",
        isEmpty && "text-muted-foreground/70 italic",
        className,
      )}
      title={String(displayValue)}
    >
      {type === "select" && isEmpty
        ? "Select..."
        : isEmpty
          ? "Empty"
          : String(displayValue)}
    </div>
  );
});
